import type { HammerConfig } from '../config/minigames.config';
import { AttemptGrade, MiniGameKind, gradeOf, type MiniGame, type MiniGameAttempt } from './MiniGame';

/** Share of the strike animation at which the head meets the nail. */
export const HAMMER_IMPACT_AT = 0.32;

/**
 * Hammer & nail (user 2026-10-04): the hammer swings back and forth over the nail, a tap strikes where it is. A
 * strike in the middle drives the nail in completely, a side hit only 10 %, beside the head nothing – at most three
 * strikes. The swing is sinusoidal (fastest over the nail), every strike makes it faster: the perfect window is a few
 * hundredths of a second.
 */
export class HammerGame implements MiniGame {
  readonly kind = MiniGameKind.Hammer;
  readonly attemptsTotal: number;
  /** Every strike: where the head came down (swing units; null = timed out) and how it went – dents in the wood. */
  readonly strikes: { readonly x: number | null; readonly grade: AttemptGrade }[] = [];
  private angle: number;
  private strikesMade = 0;
  private depthNow = 0;
  /** Strike animation: elapsed seconds, or null while swinging. */
  private striking: number | null = null;
  private strikeAt = 0;
  private judged = false;
  private attemptTime = 0;
  private lastGrade: AttemptGrade | null = null;
  private sinceGrade = Infinity;

  constructor(
    private readonly config: HammerConfig,
    private readonly round: number,
    random: () => number,
  ) {
    this.attemptsTotal = config.strikes;
    // a random start of the swing: the timing cannot be learned by heart
    this.angle = random() * Math.PI * 2;
  }

  /** Hammer head over the beam: −1 … 1, the nail at 0. Frozen where it struck while the strike runs. */
  get x(): number {
    return this.striking === null ? Math.sin(this.angle) : this.strikeAt;
  }

  /** 0 … 1 of the strike animation (down until HAMMER_IMPACT_AT, then back up), null while swinging. */
  get strike(): number | null {
    return this.striking === null ? null : Math.min(1, this.striking / this.config.strikeSeconds);
  }

  /** How far the nail is in (0 … 1). */
  get depth(): number {
    return this.depthNow;
  }

  get share(): number {
    return this.depthNow;
  }

  get grade(): AttemptGrade | null {
    return this.lastGrade;
  }

  /** Seconds since the last strike was judged (labels and sparks fade with it). */
  get secondsSinceGrade(): number {
    return this.sinceGrade;
  }

  get attemptsMade(): number {
    return this.strikesMade;
  }

  get finished(): boolean {
    return this.striking === null && (this.strikesMade >= this.config.strikes || this.depthNow >= 1);
  }

  get timeShare(): number {
    return this.striking === null ? Math.max(0, 1 - this.attemptTime / this.config.secondsPerStrike) : 1;
  }

  tap(): void {
    if (this.finished || this.striking !== null) return;
    this.strikeAt = Math.sin(this.angle);
    this.striking = 0;
    this.judged = false;
  }

  update(dt: number): MiniGameAttempt | null {
    this.sinceGrade += dt;
    if (this.finished) return null;
    if (this.striking !== null) {
      this.striking += dt;
      let attempt: MiniGameAttempt | null = null;
      if (!this.judged && this.striking >= this.config.strikeSeconds * HAMMER_IMPACT_AT) {
        this.judged = true;
        attempt = this.judge(Math.abs(this.strikeAt), this.strikeAt);
      }
      if (this.striking >= this.config.strikeSeconds) {
        this.striking = null;
        this.attemptTime = 0;
      }
      return attempt;
    }
    this.angle += ((Math.PI * 2) / this.period()) * dt;
    this.attemptTime += dt;
    if (this.attemptTime >= this.config.secondsPerStrike) {
      this.attemptTime = 0;
      return this.judge(Infinity);
    }
    return null;
  }

  /** Current swing period: the round's, faster with every strike made. */
  period(): number {
    return this.config.periods[Math.min(this.round, this.config.periods.length - 1)]! / this.config.speedUpPerStrike ** this.strikesMade;
  }

  private judge(distance: number, x: number | null = null): MiniGameAttempt {
    const grade = gradeOf(distance, this.config.perfectHalfWidth, this.config.hitHalfWidth);
    const before = this.depthNow;
    if (grade === AttemptGrade.Perfect) this.depthNow = 1;
    else if (grade === AttemptGrade.Good) this.depthNow = Math.min(1, this.depthNow + this.config.sideDrive);
    this.strikesMade += 1;
    this.strikes.push({ x, grade });
    this.lastGrade = grade;
    this.sinceGrade = 0;
    return { grade, gained: this.depthNow - before };
  }
}
